/**
 * 长耗时 Bridge 操作会话跟踪（首期仅支付）。
 * sessionStorage 兜底 WebView 进程内刷新/挂起恢复。
 */

/** 支付会话在 Web 侧的生命周期状态。 */
export type PaymentOperationState =
  | "pending"
  | "reconciling"
  | "completed"
  | "failed"
  | "cancelled";

/** 一笔进行中的 App 内购会话快照（与原生 operationId 对应）。 */
export interface PaymentOperationSession {
  operationId: string;
  storeProductId: string;
  backendProductId?: string;
  payChannel: string;
  startedAt: number;
  state: PaymentOperationState;
  productType?: "SUBS" | "INAPP";
}

const STORAGE_KEY = "edgen_bridge_payment_sessions_v1";
const MAX_SESSIONS = 5;
const SESSION_TTL_MS = 30 * 60 * 1000;

/** 从 sessionStorage 读取未过期的支付会话列表。 */
function readSessions(): PaymentOperationSession[] {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as PaymentOperationSession[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    const now = Date.now();
    return parsed.filter(
      (s) =>
        s &&
        typeof s.operationId === "string" &&
        now - (s.startedAt ?? 0) < SESSION_TTL_MS,
    );
  } catch {
    return [];
  }
}

/** 持久化支付会话（最多保留 {@link MAX_SESSIONS} 条）。 */
function writeSessions(sessions: PaymentOperationSession[]): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(sessions.slice(-MAX_SESSIONS)),
    );
  } catch {
    // ignore quota
  }
}

export const bridgeOperationTracker = {
  /**
   * 购买发起时登记会话，与原生 `operationId`（Bridge request id）对齐。
   * WebView 挂起或页面刷新后仍可被 reconcile 检索。
   */
  registerPaymentSession(
    session: Omit<PaymentOperationSession, "startedAt" | "state"> & {
      startedAt?: number;
      state?: PaymentOperationState;
    },
  ): void {
    const next: PaymentOperationSession = {
      ...session,
      startedAt: session.startedAt ?? Date.now(),
      state: session.state ?? "pending",
    };
    const sessions = readSessions().filter(
      (s) => s.operationId !== next.operationId,
    );
    sessions.push(next);
    writeSessions(sessions);
  },

  /** 更新会话状态（如 pending → reconciling → completed）。 */
  updatePaymentSession(
    operationId: string,
    patch: Partial<PaymentOperationSession>,
  ): void {
    const sessions = readSessions();
    const idx = sessions.findIndex((s) => s.operationId === operationId);
    if (idx < 0) {
      return;
    }
    sessions[idx] = { ...sessions[idx], ...patch };
    writeSessions(sessions);
  },

  /** 购买终态（成功/失败/取消）后移除会话。 */
  clearPaymentSession(operationId: string): void {
    writeSessions(readSessions().filter((s) => s.operationId !== operationId));
  },

  /** 返回仍需 reconcile 的会话（pending 或 reconciling）。 */
  getInFlightPaymentSessions(): PaymentOperationSession[] {
    return readSessions().filter(
      (s) => s.state === "pending" || s.state === "reconciling",
    );
  },

  /** 按 operationId 查询单个会话。 */
  getPaymentSession(operationId: string): PaymentOperationSession | undefined {
    return readSessions().find((s) => s.operationId === operationId);
  },

  /** 清空全部会话（主要用于单测）。 */
  clearAllPaymentSessions(): void {
    if (typeof window === "undefined") {
      return;
    }
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};
